import React from "react"

import { Action } from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { useAppDispatch, useAppSelector } from "../../../../hooks"
import { GlobalState } from "../../../../store"

import { ICheckListViewActions, ITopic } from "../.."
import { collapse, expand } from "../../slice"

import ContextMenu from "../ContextMenu"
import ActionList from "./ActionList"
import FindingList from "./FindingList"
import QuestionList from "./QuestionList"
import TopicList from "./TopicList"

export interface Props extends ITopic {
    contextMenuActions: ICheckListViewActions
}

export const Topic = ({
    id,
    title,
    label,
    controls,
    contextMenuActions,
    topics,
    questions,
    actions,
    findings,
}: Props): JSX.Element => {
    const dispatch = useAppDispatch()
    const { topicExpansion } = useAppSelector(
        (state: GlobalState) => state.audits,
    )

    const isExpanded = !!topicExpansion[id]

    const hasTopics = !!topics && topics.length > 0
    const hasQuestions = !!questions && questions.length > 0
    const hasChildrenContent = hasTopics || hasQuestions

    const hasFieldContent =
        (!!findings && findings.length > 0) || (!!actions && actions.length > 0)

    return (
        <div data-ft-component="topicContainer">
            <div
                className="theme-bubble bg-platinum has-global-hidden-actions tw-p-2"
                data-ft-component="topic"
                data-ft-is-expanded={isExpanded}
                data-ft-id={id}
                data-ft-title={title}
            >
                <div className="tw-flex">
                    <div className="tw-flex tw-flex-col tw-flex-1 tw-p-2">
                        <div
                            data-ft-component="label"
                            className="label is-dimmed"
                        >
                            {label}
                        </div>

                        {hasChildrenContent ? (
                            <Action
                                data-ft-component="expandCollapseButton"
                                onClick={(): void => {
                                    if (isExpanded) {
                                        dispatch(collapse(id))
                                    } else {
                                        dispatch(expand(id))
                                    }
                                }}
                            >
                                {isExpanded
                                    ? Symbols.GroupOpened
                                    : Symbols.Group}
                                &nbsp;
                                {title}
                            </Action>
                        ) : (
                            <div data-ft-component="title">{title}</div>
                        )}
                    </div>

                    <ContextMenu
                        type="Topic"
                        resourceId={id}
                        controls={controls}
                        contextMenuActions={contextMenuActions}
                        remark=""
                    />
                </div>

                {hasFieldContent && (
                    <div className="tw-flex tw-flex-col tw-mt-2">
                        <FindingList
                            containerElement="topicFindingsContainer"
                            contextMenuActions={contextMenuActions}
                            findings={findings}
                            isLabelShown={true}
                        />

                        <ActionList
                            actions={actions}
                            containerElement="topicActionsContainer"
                            isLabelShown={true}
                        />
                    </div>
                )}
            </div>

            {isExpanded && hasChildrenContent && (
                <>
                    <TopicList
                        contextMenuActions={contextMenuActions}
                        topics={topics}
                        containerElement="topicTopicsContainer"
                        hasIndentation={true}
                    />

                    <QuestionList
                        containerElement="topicQuestionsContainer"
                        contextMenuActions={contextMenuActions}
                        questions={questions}
                        hasIndentation={true}
                    />
                </>
            )}
        </div>
    )
}

export default Topic
